/* Katla theme JS — cart drawer (Shopify AJAX), search, menus, carousels, tilt, reveal, butterflies, wishlist */
(() => {
  "use strict";
  const fine = matchMedia("(hover:hover) and (pointer:fine)");
  const reduced = matchMedia("(prefers-reduced-motion: reduce)");
  const body = document.body;
  const opt = (name) => body.dataset[name] !== "false";

  /* Motion level comes from theme settings. "full" (the default) always shows everything.
     "auto" only steps down on genuinely ancient hardware — 4-core / 8GB laptops are NOT low-end,
     so the bar is set at 2 cores / 2GB. The real scroll cost was fixed structurally instead
     (transform-based elephant, no animated filters, no per-scroll DOM queries). */
  const motionLevel = body.dataset.motionLevel || "full";
  let lowPower = false;
  if (motionLevel === "light") {
    lowPower = true;
  } else if (motionLevel === "auto") {
    lowPower = (navigator.deviceMemory !== undefined && navigator.deviceMemory <= 2) ||
      (navigator.hardwareConcurrency !== undefined && navigator.hardwareConcurrency <= 2);
  }
  if (lowPower) document.documentElement.classList.add("low-power");

  /* ── shared toast ── */
  const note = (message) => {
    const toast = document.querySelector("#toast");
    if (!toast) return;
    clearTimeout(Number(toast.dataset.hideTimer || 0));
    toast.textContent = message;
    toast.classList.add("show");
    toast.dataset.hideTimer = String(setTimeout(() => toast.classList.remove("show"), 2800));
  };

  /* ── money ── */
  const moneyFormat = body.dataset.moneyFormat || "Rs. {{amount}}";
  const money = (cents) => moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, (cents / 100).toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2}));

  /* ── cart drawer (Shopify AJAX API) ── */
  const cartDrawer = document.querySelector("#cart-drawer");
  const cartBackdrop = document.querySelector(".cart-backdrop");
  const cartItems = document.querySelector("#cart-items");
  const cartSubtotal = document.querySelector("#cart-subtotal");
  const orderButton = document.querySelector(".order-button");
  const cartCount = document.querySelector(".cart-count");
  let cartState = null;

  const renderCart = (cart) => {
    cartState = cart;
    if (cartCount) {
      cartCount.textContent = String(cart.item_count);
      cartCount.hidden = cart.item_count === 0;
      cartCount.classList.remove("bump");
      requestAnimationFrame(() => cartCount.classList.add("bump"));
    }
    if (!cartItems) return;
    cartItems.textContent = "";
    if (!cart.items.length) {
      const empty = document.createElement("p");
      empty.className = "cart-empty";
      empty.textContent = "Your bag is waiting for something lovely.";
      cartItems.appendChild(empty);
    } else {
      const fragment = document.createDocumentFragment();
      cart.items.forEach((item) => {
        const row = document.createElement("article");
        row.className = "cart-item";
        const image = document.createElement("img");
        image.src = item.image ? item.image.replace(/(\.[a-z]+)(\?|$)/, "_144x$1$2") : "";
        image.alt = "";
        image.loading = "lazy";
        const copy = document.createElement("div");
        const name = document.createElement("p");
        name.className = "cart-item-name";
        name.textContent = item.product_title;
        const price = document.createElement("p");
        price.className = "cart-item-price";
        price.textContent = money(item.final_line_price);
        const control = document.createElement("div");
        control.className = "quantity-control";
        const mkButton = (label, action) => {
          const button = document.createElement("button");
          button.type = "button";
          button.dataset.action = action;
          button.dataset.key = item.key;
          button.setAttribute("aria-label", label + " " + item.product_title);
          button.textContent = action === "decrease" ? "−" : "+";
          return button;
        };
        const amount = document.createElement("span");
        amount.textContent = String(item.quantity);
        control.append(mkButton("Decrease quantity of", "decrease"), amount, mkButton("Increase quantity of", "increase"));
        copy.append(name, price, control);
        const remove = document.createElement("button");
        remove.type = "button";
        remove.className = "remove-item";
        remove.dataset.action = "remove";
        remove.dataset.key = item.key;
        remove.setAttribute("aria-label", "Remove " + item.product_title);
        remove.textContent = "×";
        row.append(image, copy, remove);
        fragment.appendChild(row);
      });
      cartItems.appendChild(fragment);
    }
    if (cartSubtotal) cartSubtotal.textContent = money(cart.total_price);
    if (orderButton) orderButton.disabled = cart.items.length === 0;
  };

  const cartRoot = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : "/";
  const fetchCart = () => fetch(cartRoot + "cart.js")
    .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
    .then(renderCart)
    .catch(() => {});
  const changeLine = (key, quantity) => fetch(cartRoot + "cart/change.js", {
    method: "POST",
    headers: {"Content-Type": "application/json"},
    body: JSON.stringify({id: key, quantity})
  })
    .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
    .then(renderCart)
    .catch(() => { note("Sorry, your bag could not be updated."); });

  /* Entrance animations are one-shot. Once a card has faded in we mark it, so a later hover
     or tilt (which changes the animation property) can never restart it — that restart was
     what made cards blank out and re-fade on product and collection pages. */
  document.addEventListener("animationend", (event) => {
    if (event.animationName !== "vpage-in") return;
    event.target.classList.add("is-entered");
  }, true);

  /* ── photo upload: up to N images, each on its own order-line property ── */
  const photoSlots = document.querySelector("[data-photo-slots]");
  if (photoSlots) {
    const inputs = [...photoSlots.querySelectorAll(".pdp-photo-input")];
    const addBtn = photoSlots.querySelector("[data-photo-add]");
    const counter = photoSlots.querySelector("[data-photo-counter]");
    const list = document.querySelector("[data-photo-list]");
    const max = inputs.length;
    const render = () => {
      let used = 0;
      if (list) list.textContent = "";
      inputs.forEach((input, index) => {
        const file = input.files && input.files[0];
        if (!file) return;
        used += 1;
        if (!list) return;
        const li = document.createElement("li");
        li.className = "pdp-photo-chip";
        const tick = document.createElement("span");
        tick.className = "pdp-photo-tick";
        tick.setAttribute("aria-hidden", "true");
        tick.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';
        const name = document.createElement("span");
        name.className = "pdp-photo-name";
        name.textContent = file.name;
        const remove = document.createElement("button");
        remove.type = "button";
        remove.className = "pdp-photo-clear";
        remove.setAttribute("aria-label", "Remove " + file.name);
        remove.textContent = "×";
        remove.addEventListener("click", () => {
          input.value = "";
          render();
        });
        li.append(tick, name, remove);
        list.appendChild(li);
      });
      if (counter) counter.textContent = used + "/" + max;
      if (addBtn) {
        addBtn.classList.toggle("has-photo", used > 0);
        addBtn.disabled = used >= max;
      }
    };
    if (addBtn) addBtn.addEventListener("click", () => {
      const free = inputs.find((input) => !(input.files && input.files.length));
      if (free) free.click();
    });
    inputs.forEach((input) => input.addEventListener("change", render));
    render();
  }

  /* review form: reveal the fields on the page (no WhatsApp) */
  document.addEventListener("click", (event) => {
    const toggle = event.target.closest("[data-review-toggle]");
    if (!toggle) return;
    const scope = toggle.closest("form") || document;
    const fields = scope.querySelector("[data-review-fields]");
    if (!fields) return;
    const open = fields.hidden;
    fields.hidden = !open;
    toggle.setAttribute("aria-expanded", String(open));
    if (open) {
      const first = fields.querySelector("input,select,textarea");
      if (first) first.focus();
    }
  });

  document.addEventListener("click", (event) => {
    const add = event.target.closest("[data-add-to-cart]");
    if (!add) return;
    event.preventDefault();
    const variantId = Number(add.dataset.addToCart);
    if (!variantId) return;
    /* A file can't be sent through /cart/add.js, so when the customer attached any photo
       we submit the real multipart form instead — that keeps the images on the order line. */
    const form = add.closest("form");
    const filled = form ? [...form.querySelectorAll('input[type="file"]')].some((i) => i.files && i.files.length) : false;
    if (form && filled) {
      add.disabled = true;
      form.submit();
      return;
    }
    add.disabled = true;
    fetch(cartRoot + "cart/add.js", {
      method: "POST",
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify({id: variantId, quantity: 1})
    }).then((r) => {
      if (!r.ok) throw new Error("add failed");
      return fetchCart();
    }).then(() => {
      note((add.dataset.productTitle || "Item") + " added to your bag");
    }).catch(() => {
      note("Sorry, that item could not be added.");
    }).finally(() => { add.disabled = false; });
  });

  if (cartItems) cartItems.addEventListener("click", (event) => {
    const button = event.target.closest("button[data-action]");
    if (!button || !cartState) return;
    const item = cartState.items.find((entry) => entry.key === button.dataset.key);
    if (!item) return;
    if (button.dataset.action === "increase") changeLine(item.key, item.quantity + 1);
    if (button.dataset.action === "decrease") changeLine(item.key, item.quantity - 1);
    if (button.dataset.action === "remove") changeLine(item.key, 0).then(() => note(item.product_title + " removed"));
  });

  /* focus trap shared by the drawers (parity with the original build) */
  const trapFocus = (container, event) => {
    if (event.key !== "Tab") return;
    const focusable = [...container.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select,[tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden && element.getClientRects().length);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  let cartReturnFocus = null;
  const openCart = () => {
    if (!cartDrawer) return;
    cartReturnFocus = document.activeElement;
    cartDrawer.classList.add("open");
    cartBackdrop.classList.add("open");
    cartDrawer.setAttribute("aria-hidden", "false");
    body.classList.add("cart-open");
    const close = cartDrawer.querySelector(".cart-close");
    if (close) close.focus();
  };
  const closeCart = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.remove("open");
    cartBackdrop.classList.remove("open");
    cartDrawer.setAttribute("aria-hidden", "true");
    body.classList.remove("cart-open");
    if (cartReturnFocus && document.contains(cartReturnFocus)) cartReturnFocus.focus();
  };
  if (cartDrawer) cartDrawer.addEventListener("keydown", (event) => trapFocus(cartDrawer, event));
  /* delegated so header re-renders in the Theme Editor keep working */
  document.addEventListener("click", (event) => {
    if (event.target.closest(".cart-button")) { event.preventDefault(); openCart(); }
    else if (event.target.closest(".cart-close")) closeCart();
  });
  if (cartBackdrop) cartBackdrop.addEventListener("click", closeCart);

  if (orderButton) orderButton.addEventListener("click", () => {
    if (!cartState || !cartState.items.length) return;
    if (body.dataset.checkoutMode === "whatsapp" && body.dataset.whatsapp) {
      const lines = ["Hello! I would like to order:", ""];
      cartState.items.forEach((item) => {
        lines.push(item.quantity + " × " + item.product_title + " — " + money(item.final_line_price));
        lines.push(location.origin + item.url);
        lines.push("");
      });
      lines.push("Subtotal: " + money(cartState.total_price));
      open("https://wa.me/" + body.dataset.whatsapp + "?text=" + encodeURIComponent(lines.join("\n")), "_blank", "noopener,noreferrer");
    } else {
      location.href = (window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : "/") + "checkout";
    }
  });
  fetchCart();

  /* ── menu drawer (mobile) — nodes re-queried at call time so Theme Editor re-renders survive ── */
  let menuReturnFocus = null;
  const setMenu = (openState) => {
    const drawer = document.querySelector(".menu-drawer");
    const menuBackdrop = document.querySelector(".menu-backdrop");
    const menuButton = document.querySelector(".menu-button");
    if (!drawer) return;
    if (openState) menuReturnFocus = document.activeElement;
    drawer.classList.toggle("open", openState);
    if (menuBackdrop) menuBackdrop.classList.toggle("open", openState);
    drawer.setAttribute("aria-hidden", String(!openState));
    if (menuButton) menuButton.setAttribute("aria-expanded", String(openState));
    body.classList.toggle("menu-open", openState);
    const menuClose = drawer.querySelector(".menu-close");
    if (openState && menuClose) menuClose.focus();
    else if (!openState && menuReturnFocus && document.contains(menuReturnFocus)) menuReturnFocus.focus();
  };
  document.addEventListener("click", (event) => {
    if (event.target.closest(".menu-button")) setMenu(true);
    else if (event.target.closest(".menu-close") || event.target.closest(".menu-backdrop")) setMenu(false);
  });
  document.addEventListener("keydown", (event) => {
    const drawer = document.querySelector(".menu-drawer");
    if (drawer && drawer.classList.contains("open")) trapFocus(drawer, event);
  });

  /* ── desktop nav dropdowns (delegated; markup rendered by Liquid) ── */
  const closeNavDrops = () => document.querySelectorAll(".main-nav .nav-item.open").forEach((item) => {
    item.classList.remove("open");
    const button = item.querySelector("button");
    if (button) button.setAttribute("aria-expanded", "false");
  });
  document.addEventListener("click", (event) => {
    const navButton = event.target.closest(".main-nav .nav-item > button");
    if (navButton) {
      const item = navButton.parentElement;
      const willOpen = !item.classList.contains("open");
      closeNavDrops();
      item.classList.toggle("open", willOpen);
      navButton.setAttribute("aria-expanded", String(willOpen));
      return;
    }
    const nav = document.querySelector(".main-nav");
    if (!nav || !nav.contains(event.target)) closeNavDrops();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    closeNavDrops();
    const drawer = document.querySelector(".menu-drawer");
    if (cartDrawer && cartDrawer.classList.contains("open")) closeCart();
    else if (drawer && drawer.classList.contains("open")) setMenu(false);
  });

  /* ── search dialog (client filter over embedded catalog) ── */
  const searchDialog = document.querySelector("#search-dialog");
  if (searchDialog) {
    const searchInput = searchDialog.querySelector("#site-search");
    const searchResults = searchDialog.querySelector("#search-results");
    const searchForm = searchDialog.querySelector("#search-form");
    let catalog = [];
    try { catalog = JSON.parse(document.querySelector("#katla-catalog").textContent); } catch (error) { catalog = []; }
    const renderSearch = (query) => {
      const value = (query || "").trim().toLowerCase();
      const hits = catalog.filter((entry) => !value || (entry.title + " " + entry.type).toLowerCase().includes(value)).slice(0, 8);
      searchResults.textContent = "";
      if (!hits.length) {
        const empty = document.createElement("p");
        empty.className = "search-empty";
        empty.textContent = "No match yet — press Enter to search the whole store.";
        searchResults.appendChild(empty);
        return;
      }
      const count = document.createElement("p");
      count.className = "search-result-count";
      count.textContent = value ? hits.length + (hits.length === 1 ? " result" : " results") : "Popular pieces";
      searchResults.appendChild(count);
      hits.forEach((entry) => {
        const link = document.createElement("a");
        link.className = "search-result";
        link.href = entry.url;
        const image = document.createElement("img");
        image.src = entry.image || "";
        image.alt = "";
        image.loading = "lazy";
        const copy = document.createElement("span");
        const title = document.createElement("strong");
        title.textContent = entry.title;
        const meta = document.createElement("small");
        meta.textContent = entry.price ? entry.price + " · " + entry.type : entry.type;
        copy.append(title, meta);
        const arrow = document.createElement("b");
        arrow.setAttribute("aria-hidden", "true");
        arrow.textContent = "→";
        link.append(image, copy, arrow);
        searchResults.appendChild(link);
      });
    };
    const openSearch = () => {
      renderSearch("");
      if (!searchDialog.open) searchDialog.showModal();
      requestAnimationFrame(() => searchInput.focus());
    };
    /* delegated so a re-rendered header keeps a working search button */
    document.addEventListener("click", (event) => {
      if (event.target.closest(".search-button")) openSearch();
    });
    searchDialog.querySelector(".search-close").addEventListener("click", () => searchDialog.close());
    searchDialog.addEventListener("click", (event) => { if (event.target === searchDialog) searchDialog.close(); });
    searchInput.addEventListener("input", () => renderSearch(searchInput.value));
    /* the form posts natively to the locale-aware {{ routes.search_url }} action */
    void searchForm;
    /* Ctrl/Cmd+K opens search (parity with the original build) */
    document.addEventListener("keydown", (event) => {
      const editable = /input|textarea|select/i.test(document.activeElement && document.activeElement.tagName);
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k" && !editable) {
        event.preventDefault();
        openSearch();
      }
    });
  }

  /* ── carousels ── */
  const setupCarousel = (track, previous, next) => {
    if (!track || !previous || !next) return;
    const step = () => {
      const first = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).gap) || 0;
      return first ? first.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    };
    const update = () => {
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      previous.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max - 20;
    };
    previous.addEventListener("click", () => track.scrollBy({left: -step(), behavior: reduced.matches ? "auto" : "smooth"}));
    next.addEventListener("click", () => track.scrollBy({left: step(), behavior: reduced.matches ? "auto" : "smooth"}));
    track.addEventListener("scroll", update, {passive: true});
    if ("ResizeObserver" in window) new ResizeObserver(update).observe(track);
    update();
  };
  const initCarousels = (root) => root.querySelectorAll("[data-carousel]").forEach((section) => {
    setupCarousel(
      section.querySelector("[data-carousel-track]"),
      section.querySelector("[data-carousel-prev]"),
      section.querySelector("[data-carousel-next]")
    );
  });
  initCarousels(document);
  document.addEventListener("shopify:section:load", (event) => initCarousels(event.target));

  /* ── product gallery slider + lightbox ── */
  const gallery = document.querySelector("[data-gallery]");
  if (gallery) {
    const track = gallery.querySelector("[data-gallery-track]");
    const slides = [...track.children];
    const thumbs = [...document.querySelectorAll("[data-gallery-thumb]")];
    const clamp = (i, len) => Math.max(0, Math.min(len - 1, i));
    const indexNow = () => Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
    const goTo = (i, behavior) => track.scrollTo({left: clamp(i, slides.length) * track.clientWidth, behavior: behavior || (reduced.matches ? "auto" : "smooth")});
    let galFrame = 0;
    track.addEventListener("scroll", () => {
      if (galFrame) return;
      galFrame = requestAnimationFrame(() => {
        galFrame = 0;
        const i = indexNow();
        thumbs.forEach((t, ti) => t.classList.toggle("is-active", ti === i));
      });
    }, {passive: true});
    const galPrev = gallery.querySelector("[data-gallery-prev]");
    const galNext = gallery.querySelector("[data-gallery-next]");
    if (galPrev) galPrev.addEventListener("click", () => goTo(indexNow() - 1));
    if (galNext) galNext.addEventListener("click", () => goTo(indexNow() + 1));
    thumbs.forEach((t) => t.addEventListener("click", () => goTo(Number(t.dataset.galleryThumb))));
    track.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") { event.preventDefault(); goTo(indexNow() - 1); }
      if (event.key === "ArrowRight") { event.preventDefault(); goTo(indexNow() + 1); }
    });

    const lightbox = document.querySelector("[data-lightbox]");
    if (lightbox) {
      const lbTrack = lightbox.querySelector("[data-lightbox-track]");
      const lbSlides = [...lbTrack.children];
      const lbCountEl = lightbox.querySelector("[data-lightbox-count]");
      const lbIndex = () => Math.round(lbTrack.scrollLeft / Math.max(1, lbTrack.clientWidth));
      const lbGo = (i, behavior) => lbTrack.scrollTo({left: clamp(i, lbSlides.length) * lbTrack.clientWidth, behavior: behavior || (reduced.matches ? "auto" : "smooth")});
      let lastLbIndex = 0;
      const syncCount = () => {
        lastLbIndex = lbIndex();
        if (lbCountEl) lbCountEl.textContent = (lastLbIndex + 1) + " / " + lbSlides.length;
      };
      let lbFrame = 0;
      lbTrack.addEventListener("scroll", () => {
        if (lbFrame) return;
        lbFrame = requestAnimationFrame(() => { lbFrame = 0; syncCount(); });
      }, {passive: true});
      document.addEventListener("click", (event) => {
        const opener = event.target.closest("[data-gallery-open]");
        if (!opener) return;
        lightbox.showModal();
        /* jump instantly so the clicked photo is the one that appears */
        requestAnimationFrame(() => { lbGo(Number(opener.dataset.galleryOpen), "auto"); syncCount(); lbTrack.focus(); });
      });
      const lbClose = lightbox.querySelector("[data-lightbox-close]");
      if (lbClose) lbClose.addEventListener("click", () => lightbox.close());
      lightbox.addEventListener("click", (event) => { if (event.target === lightbox) lightbox.close(); });
      const lbPrev = lightbox.querySelector("[data-lightbox-prev]");
      const lbNext = lightbox.querySelector("[data-lightbox-next]");
      if (lbPrev) lbPrev.addEventListener("click", () => lbGo(lbIndex() - 1));
      if (lbNext) lbNext.addEventListener("click", () => lbGo(lbIndex() + 1));
      lightbox.addEventListener("keydown", (event) => {
        if (event.key === "ArrowLeft") { event.preventDefault(); lbGo(lbIndex() - 1); }
        if (event.key === "ArrowRight") { event.preventDefault(); lbGo(lbIndex() + 1); }
      });
      /* keep the page gallery on the photo the customer was viewing
         (read the remembered index — a closed dialog reports scrollLeft 0) */
      lightbox.addEventListener("close", () => goTo(lastLbIndex, "auto"));
    }
  }

  /* ── wishlist + wishlist drawer ── */
const WISH_KEY = "katla.wish.v1";

const wish = (() => {
  try {
    return new Set(JSON.parse(localStorage.getItem(WISH_KEY) || "[]"));
  } catch (error) {
    return new Set();
  }
})();

const wishlistOpenButton = document.querySelector("[data-wishlist-open]");
const wishlistDrawer = document.querySelector("[data-wishlist-drawer]");
const wishlistItems = document.querySelector("[data-wishlist-items]");
const wishlistCount = document.querySelector("[data-wishlist-count]");
const wishlistSubtitle = document.querySelector("[data-wishlist-subtitle]");

const saveWishlist = () => {
  try {
    localStorage.setItem(WISH_KEY, JSON.stringify([...wish]));
  } catch (error) {}
};

const updateWishlistHearts = () => {
  document.querySelectorAll(".wish[data-wish-id]").forEach((button) => {
    const saved = wish.has(button.dataset.wishId);
    button.classList.toggle("on", saved);
    button.setAttribute("aria-pressed", String(saved));
  });
};

const updateWishlistCount = () => {
  const count = wish.size;

  if (wishlistCount) {
    wishlistCount.textContent = String(count);
    wishlistCount.hidden = count === 0;
  }

  if (wishlistSubtitle) {
    wishlistSubtitle.textContent =
      count === 1 ? "1 saved item" : `${count} saved items`;
  }
};

const renderWishlistEmpty = () => {
  if (!wishlistItems) return;

  wishlistItems.innerHTML = `
    <div class="wishlist-empty">
      <div class="wishlist-empty-icon">♡</div>
      <strong>Your wishlist is empty</strong>
      <p>Tap the heart on a product to save it here.</p>
    </div>
  `;
};

const renderWishlist = async () => {
  if (!wishlistItems) return;

  updateWishlistCount();

  if (!wish.size) {
    renderWishlistEmpty();
    return;
  }

  wishlistItems.innerHTML = `
    <div class="wishlist-items">
      ${[...wish].map((handle) => `
        <article class="wishlist-item" data-wishlist-item="${handle}">
          <div class="wishlist-item-image"></div>

          <div class="wishlist-item-info">
            <div class="wishlist-item-title">Loading…</div>
          </div>

          <button
            type="button"
            class="wishlist-item-remove"
            data-wishlist-remove="${handle}"
            aria-label="Remove from wishlist"
          >&times;</button>
        </article>
      `).join("")}
    </div>
  `;

  await Promise.all(
    [...wish].map(async (handle) => {
      try {
        const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);

        if (!response.ok) {
          throw new Error("Product could not be loaded");
        }

        const product = await response.json();

        const item = [...wishlistItems.querySelectorAll("[data-wishlist-item]")]
          .find((element) => element.dataset.wishlistItem === handle);

        if (!item) return;

        const image = product.featured_image || (product.images && product.images[0]) || "";
        const price = product.price;

        item.innerHTML = `
          <a href="/products/${encodeURIComponent(handle)}" aria-label="${product.title}">
            ${image
              ? `<img class="wishlist-item-image" src="${image}" alt="${product.title}" loading="lazy">`
              : `<div class="wishlist-item-image"></div>`
            }
          </a>

          <div class="wishlist-item-info">
            <a
              class="wishlist-item-title"
              href="/products/${encodeURIComponent(handle)}"
            >
              ${product.title}
            </a>

            <div class="wishlist-item-price">
              ${money(Number(price))}
            </div>
          </div>

          <button
            type="button"
            class="wishlist-item-remove"
            data-wishlist-remove="${handle}"
            aria-label="Remove ${product.title} from wishlist"
          >&times;</button>
        `;
      } catch (error) {
        const item = [...wishlistItems.querySelectorAll("[data-wishlist-item]")]
          .find((element) => element.dataset.wishlistItem === handle);

        if (item) item.remove();
      }
    })
  );

  updateWishlistCount();
};

const openWishlist = () => {
  if (!wishlistDrawer) return;

  document.documentElement.classList.add("wishlist-open");
  wishlistDrawer.setAttribute("aria-hidden", "false");

  if (wishlistOpenButton) {
    wishlistOpenButton.setAttribute("aria-expanded", "true");
  }

  renderWishlist();
};

const closeWishlist = () => {
  if (!wishlistDrawer) return;

  document.documentElement.classList.remove("wishlist-open");
  wishlistDrawer.setAttribute("aria-hidden", "true");

  if (wishlistOpenButton) {
    wishlistOpenButton.setAttribute("aria-expanded", "false");
  }
};

updateWishlistHearts();
updateWishlistCount();

document.addEventListener("click", (event) => {
  const wishButton = event.target.closest(".wish[data-wish-id]");

  if (wishButton) {
    const id = wishButton.dataset.wishId;
    const saved = !wish.has(id);

    if (saved) {
      wish.add(id);
    } else {
      wish.delete(id);
    }

    saveWishlist();
    updateWishlistHearts();
    updateWishlistCount();

    wishButton.classList.remove("pop");
    requestAnimationFrame(() => wishButton.classList.add("pop"));

    note(saved ? "Saved to your wishlist" : "Removed from wishlist");

    if (document.documentElement.classList.contains("wishlist-open")) {
      renderWishlist();
    }

    return;
  }

  if (event.target.closest("[data-wishlist-open]")) {
    openWishlist();
    return;
  }

  if (event.target.closest("[data-wishlist-close]")) {
    closeWishlist();
    return;
  }

  const removeButton = event.target.closest("[data-wishlist-remove]");

  if (removeButton) {
    const id = removeButton.dataset.wishlistRemove;

    if (!id) return;

    wish.delete(id);
    saveWishlist();
    updateWishlistHearts();
    updateWishlistCount();
    renderWishlist();
  }
});

document.addEventListener("keydown", (event) => {
  if (event.key === "Escape") {
    closeWishlist();
  }
});

  /* ── 3D tilt + glare ── */
  if (opt("tilt") && !lowPower) {
    let scrollEpoch = 0;
    let activeTilts = 0;
    const settles = [];
    /* counter instead of a DOM query — this listener runs on every scroll event */
    addEventListener("scroll", () => {
      scrollEpoch += 1;
      if (activeTilts > 0) settles.forEach((settle) => settle());
    }, {passive: true, capture: true});
    addEventListener("resize", () => { scrollEpoch += 1; }, {passive: true});
    document.querySelectorAll(".arrival-card,.bestseller-card,.category-card,.highlight-card,.pgrid-card,.secondary-highlights > a").forEach((card) => {
      card.classList.add("glare-host");
      let frame = 0, latest = null, rect = null, rectEpoch = -1;
      const paint = () => {
        frame = 0;
        if (!latest || !card.classList.contains("tilting")) return;
        if (!rect || rectEpoch !== scrollEpoch) {
          const had = card.style.transform;
          card.style.transition = "none";
          card.style.transform = "none";
          rect = card.getBoundingClientRect();
          card.style.transform = had;
          card.getBoundingClientRect();
          card.style.transition = "";
          rectEpoch = scrollEpoch;
        }
        if (!rect.width || !rect.height) return;
        const px = Math.min(1, Math.max(0, (latest.clientX - rect.left) / rect.width));
        const py = Math.min(1, Math.max(0, (latest.clientY - rect.top) / rect.height));
        card.style.transform = "perspective(900px) rotateX(" + ((0.5 - py) * 10).toFixed(2) + "deg) rotateY(" + ((px - 0.5) * 13).toFixed(2) + "deg) translateY(-8px) scale(1.02)";
        card.style.setProperty("--pxr", px.toFixed(3));
        card.style.setProperty("--pyr", py.toFixed(3));
      };
      card.addEventListener("pointerenter", (event) => {
        if (!fine.matches || reduced.matches || event.pointerType === "touch") return;
        rect = null;
        if (!card.classList.contains("tilting")) activeTilts += 1;
        card.classList.add("tilting");
      });
      card.addEventListener("pointermove", (event) => {
        if (!card.classList.contains("tilting")) return;
        latest = event;
        if (!frame) frame = requestAnimationFrame(paint);
      });
      const settle = () => {
        if (!card.classList.contains("tilting") && !card.style.transform) return;
        if (card.classList.contains("tilting")) activeTilts = Math.max(0, activeTilts - 1);
        card.classList.remove("tilting");
        latest = null;
        rect = null;
        card.style.transform = "";
      };
      settles.push(settle);
      card.addEventListener("pointerleave", settle);
      card.addEventListener("pointercancel", settle);
    });
  }

  /* ── scroll reveal ── */
  if (opt("revealEnabled") && !reduced.matches && "IntersectionObserver" in window) {
    const items = [];
    document.querySelectorAll("[data-reveal]:not(body) > *").forEach((item, index) => {
      item.classList.add("reveal-item");
      item.style.setProperty("--rd", Math.min(index * 70, 420) + "ms");
      items.push(item);
    });
    const done = (item) => {
      if (!item.classList.contains("reveal-item")) return;
      item.classList.add("is-in");
      setTimeout(() => {
        item.classList.remove("reveal-item", "is-in");
        item.style.removeProperty("--rd");
      }, 1500);
    };
    let firedOnce = false;
    try {
      const observer = new IntersectionObserver((entries) => {
        firedOnce = true;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          done(entry.target);
          observer.unobserve(entry.target);
        });
      }, {rootMargin: "0px 0px -36px", threshold: 0.12});
      items.forEach((item) => observer.observe(item));
      body.classList.add("reveal-armed");
      setTimeout(() => { if (!firedOnce) items.forEach(done); }, 3000);
    } catch (error) {
      items.forEach((item) => item.classList.remove("reveal-item"));
    }
  }

  /* ── butterflies ── */
  if (opt("butterflies") && !reduced.matches && !lowPower) {
    const BF = [
      {light: "#e5c39a", deep: "#a97a4e", rim: "#4d3826", bodyColor: "#3f2d1c", size: 36, cls: "bf-1", follow: false, speed: 30, dir: 1, band: 0.30, a1: 64, f1: 0.45, p1: 0, a2: 26, f2: 1.7, p2: 1.2, offset: 0},
      {light: "#e9dcbb", deep: "#b39a68", rim: "#4a3d26", bodyColor: "#3d321c", size: 27, cls: "bf-2", follow: false, speed: 24, dir: -1, band: 0.55, a1: 74, f1: 0.34, p1: 2.4, a2: 20, f2: 1.3, p2: 0.4, offset: 300},
      {light: "#e3c6bb", deep: "#a97e6d", rim: "#4a332b", bodyColor: "#3d2a22", size: 23, cls: "bf-3", follow: false, speed: 36, dir: 1, band: 0.72, a1: 54, f1: 0.5, p1: 4.1, a2: 24, f2: 2.1, p2: 2.8, offset: 620},
      {light: "#e7bd8a", deep: "#a56b3d", rim: "#46301c", bodyColor: "#3a2716", size: 32, cls: "bf-4 bf-follow", follow: true},
      {light: "#eadfc0", deep: "#ab925e", rim: "#453a22", bodyColor: "#392f1a", size: 25, cls: "bf-5 bf-follow", follow: true}
    ];
    const followEnabled = opt("butterfliesFollow") && fine.matches;
    const wingSvg = (c, uid) => {
      const grad = "bfw" + uid;
      return '<svg viewBox="0 0 34 56" aria-hidden="true"><defs><linearGradient id="' + grad + '" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="' + c.light + '"/><stop offset="1" stop-color="' + c.deep + '"/></linearGradient></defs>' +
        '<path d="M33 26 C28 13, 18 3, 8 2.6 C2.2 2.4, .8 8.4, 4.4 14 C9.6 21.4, 22 25.6, 33 27.4 Z" fill="' + c.rim + '"/>' +
        '<path d="M32.4 26.4 C27.6 15, 18.4 5.6, 9.6 5.2 C4.6 5, 3.6 9.6, 6.8 14.2 C11.4 20.6, 22 24.6, 32.4 27 Z" fill="url(#' + grad + ')"/>' +
        '<path d="M33 29 C24 28.8, 11.6 31.4, 8 39 C5.4 44.8, 10 51, 17 50 C25.4 48.6, 32 39.6, 33 31.4 Z" fill="' + c.rim + '"/>' +
        '<path d="M32.4 30 C24.6 29.9, 13.6 32.2, 10.6 38.6 C8.6 43.4, 12.2 47.9, 17.6 47 C24.6 45.8, 31.4 38.4, 32.4 31.6 Z" fill="url(#' + grad + ')"/>' +
        '<g stroke="' + c.rim + '" stroke-width=".5" opacity=".35" fill="none"><path d="M33 26.6 C26 21, 18 12, 10 6.4"/><path d="M33 27 C25.6 23.4, 17 17.2, 8 12"/><path d="M33 27.6 C26.4 26.2, 17.6 23.4, 9.4 18.2"/><path d="M33 29.8 C26 30.6, 18 33.4, 12.6 37.6"/><path d="M33 30.8 C27.4 33.6, 21.4 39.4, 18.2 45"/></g>' +
        '<g fill="#f7efdd" opacity=".9"><circle cx="6.2" cy="4.8" r=".8"/><circle cx="3.4" cy="9.4" r=".7"/><circle cx="3" cy="13.4" r=".6"/><circle cx="7.6" cy="43.4" r=".75"/><circle cx="12.2" cy="48.8" r=".65"/></g>' +
        '<path d="M30.6 26 C26 18, 19.6 10.6, 12.6 8.2 C18 13.4, 25 20.4, 30.6 26 Z" fill="#fff6e6" opacity=".22"/></svg>';
    };
    const bodySvg = (c) => '<svg viewBox="0 0 12 56" aria-hidden="true"><ellipse cx="6" cy="31" rx="1.5" ry="9.5" fill="' + c.bodyColor + '"/><ellipse cx="6" cy="21.5" rx="1.9" ry="4" fill="' + c.bodyColor + '"/><circle cx="6" cy="15.5" r="2" fill="' + c.bodyColor + '"/><path d="M5 14 C3.5 9.5, 2.2 7.5, 1.2 6.5" fill="none" stroke="' + c.bodyColor + '" stroke-width=".8" stroke-linecap="round"/><path d="M7 14 C8.5 9.5, 9.8 7.5, 10.8 6.5" fill="none" stroke="' + c.bodyColor + '" stroke-width=".8" stroke-linecap="round"/><circle cx="1.2" cy="6.2" r=".7" fill="' + c.bodyColor + '"/><circle cx="10.8" cy="6.2" r=".7" fill="' + c.bodyColor + '"/></svg>';
    /* All butterflies live inside one fixed, clipped layer. Their flight path reaches past the
       right edge, and some mobile browsers count that as page width — this guarantees they can
       never make the page scroll sideways. */
    const flockLayer = document.createElement("div");
    flockLayer.className = "katla-flock";
    flockLayer.setAttribute("aria-hidden", "true");
    body.appendChild(flockLayer);
    const ambient = [];
    const trail = [];
    BF.filter((c) => followEnabled || !c.follow).forEach((c, index) => {
      const el = document.createElement("div");
      el.className = "butterfly " + c.cls;
      el.style.setProperty("--bfs", c.size + "px");
      el.setAttribute("aria-hidden", "true");
      el.innerHTML = '<div class="bf-inner"><div class="bf-wing bf-wl">' + wingSvg(c, index * 2 + 1) + '</div><div class="bf-wing bf-wr">' + wingSvg(c, index * 2 + 2) + '</div><div class="bf-body">' + bodySvg(c) + '</div></div>';
      el.style.transform = "translate(-200px,-200px)";
      flockLayer.appendChild(el);
      if (c.follow) trail.push(el);
      else ambient.push(Object.assign({el, prevY: undefined}, c));
    });
    const followers = trail.length === 2 ? [
      {el: trail[0], ease: 0.05, ox: -52, oy: -46, speed: 1.05, phase: 0, x: innerWidth * 0.3, y: -120},
      {el: trail[1], ease: 0.032, ox: 48, oy: -60, speed: 0.75, phase: 2.1, x: innerWidth * 0.7, y: -120}
    ] : [];
    let mx = innerWidth / 2, my = innerHeight / 3, tracking = false;
    addEventListener("pointermove", (event) => {
      if (event.pointerType === "touch") return;
      mx = event.clientX;
      my = event.clientY;
      tracking = true;
    }, {passive: true});
    const fly = (time) => {
      const t = time / 1000;
      ambient.forEach((b) => {
        const span = innerWidth + 200;
        const prog = ((t * b.speed + b.offset) % span + span) % span;
        const x = (b.dir > 0 ? prog : span - prog) - 100 + Math.sin(t * 0.7 + b.p2) * 22;
        const y = innerHeight * b.band + Math.sin(t * b.f1 + b.p1) * b.a1 + Math.sin(t * b.f2 + b.p2) * b.a2;
        const lean = b.prevY === undefined ? 0 : Math.max(-22, Math.min(22, (y - b.prevY) * 9 * b.dir));
        b.prevY = y;
        b.el.style.transform = "translate(" + x.toFixed(1) + "px," + y.toFixed(1) + "px) rotate(" + lean.toFixed(1) + "deg)" + (b.dir < 0 ? " scaleX(-1)" : "");
      });
      if (tracking) followers.forEach((f) => {
        const ft = t * f.speed + f.phase;
        const targetX = mx + f.ox + Math.sin(ft) * 16;
        const targetY = my + f.oy + Math.cos(ft * 1.4) * 12;
        f.x += (targetX - f.x) * f.ease;
        f.y += (targetY - f.y) * f.ease;
        const lean = Math.max(-16, Math.min(16, (targetX - f.x) * 0.06));
        f.el.style.transform = "translate(" + f.x.toFixed(1) + "px," + f.y.toFixed(1) + "px) rotate(" + lean.toFixed(1) + "deg)";
      });
      requestAnimationFrame(fly);
    };
    requestAnimationFrame(fly);
  }
    /* Smart product-page back button: return to the exact page the customer came from. */
  document.querySelectorAll("[data-smart-back]").forEach((link) => {
  const label = link.querySelector("[data-back-label]");
  const referrer = document.referrer;

  if (label && referrer) {
    try {
      const refUrl = new URL(referrer);

      if (refUrl.origin === window.location.origin) {
        if (refUrl.pathname.startsWith("/collections/combos")) {
          label.textContent = "BACK TO COMBOS";
        }
      }
    } catch (error) {
      /* Keep the default label. */
    }
  }

  link.addEventListener("click", (event) => {
    if (!referrer) return;

    try {
      const refUrl = new URL(referrer);

      if (refUrl.origin === window.location.origin && history.length > 1) {
        event.preventDefault();
        history.back();
      }
    } catch (error) {
      /* Keep the normal fallback link if the referrer cannot be parsed. */
    }
  });
});
})();
